import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { isValidEmail } from '../lib/emailValidation';
import Modal from '../components/common/Modal';
import { TermsContent } from './TermsPage';
import { PrivacyContent } from './PrivacyPage';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const modeParam = searchParams.get('mode');
  const [view, setView] = useState(modeParam === 'signup' ? 'signup' : 'login');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [honeypot, setHoneypot] = useState(''); // Honeypot field for bot detection
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showSignupSuccessModal, setShowSignupSuccessModal] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
  const [shouldRenderTurnstile, setShouldRenderTurnstile] = useState(false);
  const [autoSubmitAfterTurnstile, setAutoSubmitAfterTurnstile] = useState(false);
  const turnstileContainerRef = useRef(null);
  const navigate = useNavigate();
  const [legalModalType, setLegalModalType] = useState(null); // 'terms' | 'privacy' | null

  // Always start at top when entering login/signup
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, []);

  // Also scroll to top when toggling between login/signup views
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [view]);

  // Plan checkout URLs mapping
  const planCheckoutUrls = {
    start:
      'https://goodlink.lemonsqueezy.com/checkout/buy/315d0e60-5a87-44f0-90c7-7f7789aa85a0?embed=1',
    advanced:
      'https://goodlink.lemonsqueezy.com/checkout/buy/f544686f-e91d-425b-a930-22d293fcd74e?embed=1',
    pro: 'https://goodlink.lemonsqueezy.com/checkout/buy/a3649238-094e-4217-af3c-3b1f996f3c80?embed=1',
  };

  // Function to open Lemon Squeezy checkout or customer portal
  const openCheckout = async (planName) => {
    const checkoutUrl = planCheckoutUrls[planName.toLowerCase()];
    if (!checkoutUrl) return;

    // Get user ID
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    let profileEmail = null;

    // Check user profile to see if they have a paid plan (and get email from profiles)
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('plan_type, lemon_squeezy_customer_portal_url, email')
        .eq('user_id', user.id)
        .single();

      // If user has a paid plan (not FREE) and has customer portal URL, open it
      if (profile && profile.plan_type !== 'free' && profile.lemon_squeezy_customer_portal_url) {
        const portalUrl = String(profile.lemon_squeezy_customer_portal_url).trim();
        if (portalUrl) {
          window.open(portalUrl, '_blank', 'noopener,noreferrer');
          return;
        }
      }
      if (profile?.email) {
        profileEmail = String(profile.email).trim();
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    }

    // Otherwise, if user is on FREE plan, open checkout
    const baseUrl = checkoutUrl.split('?')[0];
    const q = [];
    const emailToUse = (profileEmail || user.email || '').trim();
    console.log('[LS Checkout][Auth] plan=', planName, 'emailToUse=', emailToUse, 'user.email=', user?.email, 'profileEmail=', profileEmail);
    if (emailToUse) {
      q.push(`checkout[email]=${encodeURIComponent(emailToUse)}`);
    }
    q.push(`checkout[custom][user_id]=${encodeURIComponent(user.id)}`);
    q.push('embed=1');
    const finalUrl = `${baseUrl}?${q.join('&')}`;
    console.log('[LS Checkout][Auth] finalUrl=', finalUrl);
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  // Check if user is already logged in when component mounts with plan param
  useEffect(() => {
    if (!planParam || !supabase) return;

    const checkExistingUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        // User is already logged in, open checkout immediately
        await openCheckout(planParam);
      }
    };

    checkExistingUser();
  }, [planParam]);

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError(null);

      // Save plan to sessionStorage for OAuth redirect
      if (planParam) {
        sessionStorage.setItem('pendingPlan', planParam);
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/login${planParam ? `?plan=${planParam}` : ''}`,
        },
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
      sessionStorage.removeItem('pendingPlan');
    } finally {
      setLoading(false);
    }
  };

  // Sync full_name from Google (or other OAuth) user_metadata to profiles
  const syncProfileFullName = async (user) => {
    if (!user) return;
    const name = user.user_metadata?.full_name || user.user_metadata?.name;
    if (!name || typeof name !== 'string' || !name.trim()) return;
    try {
      await supabase.from('profiles').update({ full_name: name.trim() }).eq('user_id', user.id);
    } catch (err) {
      console.warn('Could not sync full_name to profile:', err);
    }
  };

  // Check for pending plan after OAuth redirect; sync Google name to profile, then go to dashboard
  useEffect(() => {
    const pendingPlan = sessionStorage.getItem('pendingPlan');
    if (supabase) {
      const checkUser = async () => {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          await syncProfileFullName(user);
          if (pendingPlan) {
            await openCheckout(pendingPlan);
            sessionStorage.removeItem('pendingPlan');
            setTimeout(() => {
              navigate('/dashboard/links');
            }, 2000);
          } else {
            navigate('/dashboard/links');
          }
        }
      };
      checkUser();
    }
  }, [navigate]);

  // Initialize Turnstile widget only after all signup validations passed
  useEffect(() => {
    let currentWidgetId = null;
    let timer = null;

    if (view !== 'signup' || !shouldRenderTurnstile) {
      // Cleanup when leaving signup view or when we shouldn't render Turnstile yet
      if (turnstileWidgetId && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId);
        } catch (error) {
          // Ignore errors if widget already removed
        }
        setTurnstileWidgetId(null);
      }
      setTurnstileToken(null);
      return;
    }

    if (!window.turnstile) {
      console.warn('Turnstile script not loaded yet');
      return;
    }

    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (!siteKey) {
      console.warn(
        'Turnstile Site Key not found. Please add VITE_TURNSTILE_SITE_KEY to your environment variables.'
      );
      return;
    }

    // Reset token when switching to signup
    setTurnstileToken(null);

    // Wait for the DOM element to be available (wait for animation to complete)
    const checkAndRender = () => {
      const container = turnstileContainerRef.current;
      if (!container) {
        // Retry after a short delay if container not found
        timer = setTimeout(checkAndRender, 100);
        return;
      }

      // Check if widget already exists in the container (cleanup first)
      const existingWidget = container.querySelector('.cf-turnstile');
      if (existingWidget) {
        try {
          // Try to get widget ID from the element
          const widgetIdAttr = existingWidget.getAttribute('data-widget-id');
          if (widgetIdAttr && window.turnstile) {
            window.turnstile.remove(widgetIdAttr);
          }
        } catch (error) {
          // Ignore errors
        }
        // Clear the container
        container.innerHTML = '';
      }

      // Also cleanup previous widget from state if exists
      if (turnstileWidgetId && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId);
        } catch (error) {
          // Ignore if already removed
        }
      }

      // Render Turnstile widget with the actual HTMLElement
      try {
        const widgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          language: 'auto',
          callback: (token) => {
            setTurnstileToken(token);
          },
          'error-callback': () => {
            setTurnstileToken(null);
            setError('Security verification failed. Please try again.');
            // Reset the widget so user can try again
            setTimeout(() => {
              if (window.turnstile && widgetId) {
                try {
                  window.turnstile.reset(widgetId);
                } catch (e) {
                  // Ignore errors
                }
              }
            }, 500);
          },
          'expired-callback': () => {
            setTurnstileToken(null);
            // Reset the widget when token expires
            if (window.turnstile && widgetId) {
              try {
                window.turnstile.reset(widgetId);
              } catch (e) {
                // Ignore errors
              }
            }
          },
        });

        currentWidgetId = widgetId;
        setTurnstileWidgetId(widgetId);
      } catch (error) {
        console.error('Error rendering Turnstile widget:', error);
      }
    };

    // Wait a bit for the animation to complete and DOM to be ready
    timer = setTimeout(checkAndRender, 350);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      // Cleanup: remove widget using the current widget ID
      if (currentWidgetId && window.turnstile) {
        try {
          window.turnstile.remove(currentWidgetId);
        } catch (error) {
          // Ignore errors if widget already removed
        }
      } else if (turnstileWidgetId && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId);
        } catch (error) {
          // Ignore errors if widget already removed
        }
      }
    };
  }, [view, shouldRenderTurnstile]);

  // When Turnstile token is ready and auto-submit is requested, complete signup automatically
  useEffect(() => {
    const runAutoSubmit = async () => {
      if (
        view !== 'signup' ||
        !shouldRenderTurnstile ||
        !turnstileToken ||
        !autoSubmitAfterTurnstile ||
        loading
      ) {
        return;
      }

      try {
        setLoading(true);

        const turnstileWorkerUrl =
          import.meta.env.VITE_TURNSTILE_WORKER_URL ||
          'https://turnstile-verification.fancy-sky-7888.workers.dev';
        const verifyResponse = await fetch(`${turnstileWorkerUrl}/api/verify-turnstile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: turnstileToken,
          }),
        });

        if (!verifyResponse.ok) {
          const errorData = await verifyResponse.json().catch(() => ({}));
          throw new Error(
            errorData.error || 'Security verification failed. Please try again.'
          );
        }

        const verifyResult = await verifyResponse.json();
        if (!verifyResult.success) {
          throw new Error('Security verification failed. Please try again.');
        }

        // Only proceed with signup if Turnstile verification passed
        const trimmedName = (fullName || '').trim();
        const { error, data } = await supabase.auth.signUp({
          email,
          password,
          options: {
            // Full name was already validated above (length + allowed characters)
            data: { full_name: trimmedName || undefined },
            emailRedirectTo: `${window.location.origin}/login${
              planParam ? `?plan=${planParam}` : ''
            }`,
          },
        });

        if (error) {
          if (error.message && error.message.includes('confirmation email')) {
            throw new Error(
              'Email configuration error. Please contact support or check your Supabase email settings.'
            );
          }
          throw error;
        }

        // If Supabase returns a user with empty identities, it usually means the email already exists
        // (Supabase may do this to prevent user enumeration).
        if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
          throw new Error('This email already exists. Please Sign In.');
        }

        if (data?.user && !data?.session) {
          setShowSignupSuccessModal(true);
          setMessage(null);
        } else if (data?.session) {
          navigate('/dashboard/links');
        }
      } catch (err) {
        let message =
          err instanceof Error ? err.message : 'Something went wrong. Please try again.';

        // Friendly message when trying to sign up with an email that already exists
        if (
          err instanceof Error &&
          typeof err.message === 'string' &&
          /(already.*(registered|exists|used))|(user already registered)|(duplicate)|(email.*(exists|registered))/i.test(
            err.message
          )
        ) {
          message = 'This email already exists. Please Sign In.';
        }

        setError(message);
      } finally {
        setLoading(false);
        setAutoSubmitAfterTurnstile(false);
      }
    };

    runAutoSubmit();
  }, [
    autoSubmitAfterTurnstile,
    shouldRenderTurnstile,
    turnstileToken,
    view,
    loading,
    email,
    password,
    fullName,
    planParam,
    navigate,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (view === 'login') {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;

        // If plan is present, open checkout, then go to Link Manager
        if (planParam && data?.user) {
          await openCheckout(planParam);
          setTimeout(() => {
            navigate('/dashboard/links');
          }, 2000);
        } else {
          // Regular login -> Link Manager
          navigate('/dashboard/links');
        }
      } else if (view === 'signup') {
        // Full name validation
        const trimmedName = fullName.trim();
        if (!trimmedName || trimmedName.length < 2) {
          throw new Error(
            'Your full name is required so we can personalize your Goodlink experience.'
          );
        }
        if (trimmedName.length > 20) {
          throw new Error('Full name cannot exceed 20 characters');
        }

        // Structured validation on full name (allowed chars + no multiple hyphens)
        const { sanitizeInput } = await import('../lib/inputSanitization');
        const nameCheck = sanitizeInput(trimmedName);
        if (!nameCheck.safe) {
          throw new Error(nameCheck.error);
        }

        // Email format validation (professional validation: length, regex, edge cases)
        if (!isValidEmail(email)) {
          throw new Error('Please enter a valid email address (e.g. name@example.com)');
        }

        // Password validation
        if (!password) {
          throw new Error('Please enter a password.');
        }

        if (password.length < 8) {
          throw new Error('Password is not valid.');
        }

        if (password.length > 15) {
          throw new Error('Password is not valid.');
        }

        // Check for at least one uppercase letter, one lowercase letter, and one number
        const hasUpper = /[A-Z]/.test(password);
        const hasLower = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);

        if (!hasUpper || !hasLower || !hasNumber) {
          throw new Error('Password is not valid.');
        }

        if (!confirmPassword) {
          throw new Error('Please confirm your password.');
        }

        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }

        // Honeypot check - if this field is filled, it's a bot
        // Check DOM element directly (bots might fill it directly, bypassing React state)
        const honeypotField = document.querySelector('input[name="website"]');
        const honeypotValueFromDOM = honeypotField?.value || '';
        const honeypotValue = honeypotValueFromDOM || honeypot;

        if (honeypotValue && honeypotValue.trim() !== '') {
          // Silently block bot without revealing why
          console.warn('Bot detected via honeypot field');
          setError('Registration failed. Please try again.');
          setLoading(false);
          return;
        }

        // Trigger Turnstile only after all validations pass
        if (!shouldRenderTurnstile) {
          setShouldRenderTurnstile(true);
          setAutoSubmitAfterTurnstile(true);
          setLoading(false);
          return;
        }
      } else if (view === 'forgot-password') {
        if (!isValidEmail(email)) {
          throw new Error('Please enter a valid email address (e.g. name@example.com)');
        }
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setMessage('Password reset link sent to your email.');
      }
    } catch (err) {
      setError(err.message);

      // Reset Turnstile widget if signup/login failed, so user can try again
      if (view === 'signup' && turnstileWidgetId && window.turnstile) {
        try {
          window.turnstile.reset(turnstileWidgetId);
          setTurnstileToken(null);
        } catch (e) {
          // If reset fails, try removing and re-rendering
          try {
            window.turnstile.remove(turnstileWidgetId);
            setTurnstileWidgetId(null);
            // Clear container to force re-render
            const container = turnstileContainerRef.current;
            if (container) {
              container.innerHTML = '';
            }
          } catch (removeError) {
            // Ignore errors
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Password Strength Meter Component
  const PasswordStrengthMeter = ({ password }) => {
    if (!password) return null;

    // Check functions
    const checks = {
      length: password.length >= 8,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
    };

    // If password already meets all requirements, hide the helper UI
    if (checks.length && checks.hasUpper && checks.hasLower && checks.hasNumber) {
      return null;
    }

    // Calculate score (0 to 4)
    const strengthScore = Object.values(checks).filter(Boolean).length;

    // Colors by strength
    const strengthColor = () => {
      if (strengthScore === 0) return 'bg-slate-600';
      if (strengthScore === 1) return 'bg-red-500';
      if (strengthScore === 2) return 'bg-orange-500';
      if (strengthScore === 3) return 'bg-yellow-500';
      return 'bg-green-500';
    };

    // Text by strength
    const strengthText = () => {
      if (strengthScore === 0) return '';
      if (strengthScore === 1) return 'Very Weak';
      if (strengthScore === 2) return 'Weak';
      if (strengthScore === 3) return 'Medium';
      return 'Strong';
    };

    return (
      <div id="password-strength" role="status" aria-live="polite" className="mt-2 w-full">
        <div className="flex gap-1 mb-2" aria-hidden="true">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`h-1.5 w-full rounded-full transition-colors duration-300 ${
                step <= strengthScore ? strengthColor() : 'bg-slate-700'
              }`}
            />
          ))}
        </div>

        {/* Requirements checklist */}
        <ul className="text-xs space-y-1 text-[#1b1b1b]">
          <li
            className={`flex items-center gap-2 ${
              checks.length ? 'text-green-500 font-medium' : ''
            }`}
          >
            <span>{checks.length ? '[x]' : '[ ]'}</span>
            <span>At least 8 characters</span>
          </li>
          <li
            className={`flex items-center gap-2 ${
              checks.hasUpper ? 'text-green-500 font-medium' : ''
            }`}
          >
            <span>{checks.hasUpper ? '[x]' : '[ ]'}</span>
            <span>One uppercase letter (A-Z)</span>
          </li>
          <li
            className={`flex items-center gap-2 ${
              checks.hasLower ? 'text-green-500 font-medium' : ''
            }`}
          >
            <span>{checks.hasLower ? '[x]' : '[ ]'}</span>
            <span>One lowercase letter (a-z)</span>
          </li>
          <li
            className={`flex items-center gap-2 ${
              checks.hasNumber ? 'text-green-500 font-medium' : ''
            }`}
          >
            <span>{checks.hasNumber ? '[x]' : '[ ]'}</span>
            <span>At least one number</span>
          </li>
        </ul>

        {/* Strength text */}
        {strengthScore > 0 && (
          <p
            className={`text-xs mt-2 font-medium ${
              strengthScore === 1
                ? 'text-red-500'
                : strengthScore === 2
                  ? 'text-orange-500'
                  : strengthScore === 3
                    ? 'text-yellow-500'
                    : 'text-green-500'
            }`}
          >
            Password strength: {strengthText()}
          </p>
        )}
      </div>
    );
  };

  const Logo = () => (
    <Link to="/" className="flex items-center gap-3 mb-8 transition-colors hover:text-[#0b996f]">
      {/* Link-shape icon hidden by request (kept in code, do not delete)
      <div className="size-5 sm:size-8 text-primary flex-shrink-0" aria-hidden="true">
        <svg fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
            stroke="#a855f7"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="4"
          ></path>
          <path
            d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
            stroke="#a855f7"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="4"
          ></path>
        </svg>
      </div>
      */}
      <span className="text-3xl font-black leading-tight tracking-tight text-inherit">GoodLink</span>
    </Link>
  );

  return (
    <div className="min-h-screen w-full bg-[#d7fec8] flex flex-col items-center px-6 pt-10 md:pt-16 pb-12 relative overflow-hidden">
      <a href="#auth-main" className="skip-to-content">
        Skip to main content
      </a>
      <div className="absolute top-[-10%] left-[-10%] size-96 bg-primary/10 blur-[120px] rounded-full" aria-hidden="true"></div>
      <div className="absolute bottom-[-10%] right-[-10%] size-96 bg-secondary-green/30 blur-[120px] rounded-full" aria-hidden="true"></div>

      <main id="auth-main" className="relative z-10 w-full max-w-md flex flex-col items-center">
        <Logo />

        <div className="w-full bg-white backdrop-blur-xl border border-[#d7fec8]/60 p-8 rounded-3xl shadow-2xl relative shadow-[0_0_40px_rgba(168,85,247,0.35)]">
          {planParam && (
            <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-lg text-center">
              <p className="text-sm text-primary font-bold">
                Complete your {planParam.toUpperCase()} plan purchase
              </p>
            </div>
          )}
          <AnimatePresence mode="wait">
            {view === 'login' && (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col gap-6"
              >
                <div className="text-center mb-2">
                  <h1 className="text-2xl font-bold text-[#1b1b1b] mb-2">Welcome Back</h1>
                  <p className="text-[#1b1b1b]">Log in to your GoodLink.ai account</p>
                </div>

                {error && (
                  <div role="alert" className="bg-red-500/10 border border-red-500/20 text-red-500 text-sm p-3 rounded-lg text-center animate-in fade-in zoom-in duration-200">
                    {error}
                  </div>
                )}

                {message && (
                  <div role="status" className="bg-green-500/10 border border-green-500/20 text-green-700 text-sm p-3 rounded-lg text-center animate-in fade-in zoom-in duration-200">
                    {message}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="login-email" className="text-sm font-bold text-[#1b1b1b] ml-1">Email Address</label>
                    <input
                      id="login-email"
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="name@example.com"
                      className="h-12 w-full bg-white border border-slate-200 rounded-xl px-4 text-[#1b1b1b] focus:outline-none focus:border-primary transition-colors"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center ml-1">
                      <label htmlFor="login-password" className="text-sm font-bold text-[#1b1b1b]">Password</label>
                      <button
                        type="button"
                        onClick={() => {
                          setView('forgot-password');
                          setHoneypot('');
                          setError(null);
                          setMessage(null);
                        }}
                        className="text-xs text-primary hover:text-primary/80 font-bold transition-colors"
                      >
                        Forgot?
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="********"
                        className="h-12 w-full bg-white border border-slate-200 rounded-xl px-4 pr-12 text-[#1b1b1b] focus:outline-none focus:border-primary transition-colors"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#10b981] hover:text-[#10b981]/80 transition-colors"
                      >
                        {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="h-12 w-full bg-[#d7fec8] hover:bg-[#c9f3b9] text-[#1b1b1b] font-bold rounded-xl transition-all shadow-lg shadow-[#d7fec8]/40 mt-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading && (
                      <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true"></div>
                    )}
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>

                <div className="relative flex items-center justify-center my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <span className="relative z-10 bg-white px-4 text-xs text-slate-500 font-bold uppercase tracking-widest">
                    Or continue with
                  </span>
                </div>

                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="h-12 w-full bg-secondary-green/50 hover:bg-[#c9f3b9] border border-primary/20 text-[#1b1b1b] font-bold rounded-xl flex items-center justify-center gap-3 transition-all disabled:opacity-50"
                >
                  <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Google
                </button>

                <p className="text-center text-sm text-[#1b1b1b] mt-2">
                  Don't have an account?{' '}
                  <button
                    onClick={() => {
                      setView('signup');
                      setError(null);
                      setMessage(null);
                      setHoneypot('');
                    }}
                    className="text-primary hover:text-primary/80 font-bold transition-colors underline-offset-4 hover:underline"
                  >
                    Create one for free
                  </button>
                </p>
              </motion.div>
            )}

            {view === 'signup' && (
              <motion.div
                key="signup"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col gap-6"
              >
                <div className="text-center mb-2">
                  <h1 className="text-2xl font-bold text-[#1b1b1b] mb-2">Join GoodLink.ai</h1>
                  <p className="text-[#1b1b1b]">Get Your GoodLink's Now</p>
                </div>

                {error && (
                  <div role="alert" className="bg-red-500/10 border border-red-500/20 text-red-500 text-sm p-3 rounded-lg text-center">
                    {error}
                  </div>
                )}

                {message && view !== 'signup' && (
                  <div role="status" className="bg-green-500/10 border border-green-500/20 text-green-700 text-sm p-3 rounded-lg text-center">
                    {message}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="signup-name" className="text-sm font-bold text-[#1b1b1b] ml-1">Full Name</label>
                    <input
                      id="signup-name"
                      type="text"
                      placeholder="Your full name"
                      autoComplete="name"
                      className="h-12 w-full bg-white border border-slate-200 rounded-xl px-4 text-[#1b1b1b] focus:outline-none focus:border-primary transition-colors"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="signup-email" className="text-sm font-bold text-[#1b1b1b] ml-1">Email Address</label>
                    <input
                      id="signup-email"
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="name@example.com"
                      className="h-12 w-full bg-white border border-slate-200 rounded-xl px-4 text-[#1b1b1b] focus:outline-none focus:border-primary transition-colors"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="signup-password" className="text-sm font-bold text-[#1b1b1b] ml-1">Password</label>
                    <div className="relative">
                      <input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="********"
                        aria-describedby="password-strength"
                        className="h-12 w-full bg-white border border-slate-200 rounded-xl px-4 pr-12 text-[#1b1b1b] focus:outline-none focus:border-primary transition-colors"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#10b981] hover:text-[#10b981]/80 transition-colors"
                      >
                        {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                      </button>
                    </div>
                    {view === 'signup' && <PasswordStrengthMeter password={password} />}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="signup-confirm-password" className="text-sm font-bold text-[#1b1b1b] ml-1">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        id="signup-confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="********"
                        className="h-12 w-full bg-white border border-slate-200 rounded-xl px-4 pr-12 text-[#1b1b1b] focus:outline-none focus:border-primary transition-colors"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#10b981] hover:text-[#10b981]/80 transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                      </button>
                    </div>
                  </div>

      {/* Signup success modal using shared Modal component */}
      <Modal
        isOpen={showSignupSuccessModal}
        onClose={() => setShowSignupSuccessModal(false)}
        title="Registration completed successfully"
        message={
          <p className="text-sm text-slate-600 leading-relaxed">
            Check your email for the confirmation link! If you don&apos;t receive it, check your
            spam folder.
          </p>
        }
        type="success"
        confirmText="Got it"
        onConfirm={() => setShowSignupSuccessModal(false)}
        confirmButtonClass="btn-primary"
      />

                  {/* Honeypot field - hidden from humans, traps bots */}
                  <input
                    type="text"
                    name="website"
                    id="website"
                    defaultValue=""
                    tabIndex={-1}
                    autoComplete="off"
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      opacity: 0,
                      pointerEvents: 'auto', // Allow bots to fill it
                      zIndex: -1,
                      width: '1px',
                      height: '1px',
                    }}
                    aria-hidden="true"
                  />

                  {/* Turnstile Widget - only shown after all validations pass */}
                  {shouldRenderTurnstile && (
                    <div
                      ref={turnstileContainerRef}
                      id="turnstile-widget"
                      className="flex justify-center min-h-[65px]"
                    ></div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="h-12 w-full bg-[#d7fec8] hover:bg-[#c9f3b9] text-[#1b1b1b] font-bold rounded-xl transition-all shadow-lg shadow-[#d7fec8]/40 mt-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading && (
                      <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true"></div>
                    )}
                    {loading ? 'Creating account...' : 'Create Account'}
                  </button>
                </form>

                <div className="relative flex items-center justify-center my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <span className="relative z-10 bg-white px-4 text-xs text-slate-500 font-bold uppercase tracking-widest">
                    Or join with
                  </span>
                </div>

                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="h-12 w-full bg-secondary-green/50 hover:bg-[#c9f3b9] border border-primary/20 text-[#1b1b1b] font-bold rounded-xl flex items-center justify-center gap-3 transition-all disabled:opacity-50"
                >
                  <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Google
                </button>

                <p className="text-center text-sm text-[#1b1b1b] mt-2">
                  Already have an account?{' '}
                  <button
                    onClick={() => {
                      setView('login');
                      setError(null);
                      setMessage(null);
                      setHoneypot('');
                    }}
                    className="text-primary hover:text-primary/80 font-bold transition-colors underline-offset-4 hover:underline"
                  >
                    Sign In
                  </button>
                </p>
              </motion.div>
            )}

            {view === 'forgot-password' && (
              <motion.div
                key="forgot"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col gap-6"
              >
                <div className="text-center mb-2">
                  <h1 className="text-2xl font-bold text-[#1b1b1b] mb-2">Reset Password</h1>
                  <p className="text-[#1b1b1b]">We'll send you recovery instructions</p>
                </div>

                {error && (
                  <div role="alert" className="bg-red-500/10 border border-red-500/20 text-red-500 text-sm p-3 rounded-lg text-center">
                    {error}
                  </div>
                )}

                {message && (
                  <div role="status" className="bg-green-500/10 border border-green-500/20 text-green-700 text-sm p-3 rounded-lg text-center">
                    {message}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="forgot-email" className="text-sm font-bold text-[#1b1b1b] ml-1">Email Address</label>
                    <input
                      id="forgot-email"
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="name@example.com"
                      className="h-12 w-full bg-white border border-slate-200 rounded-xl px-4 text-[#1b1b1b] focus:outline-none focus:border-primary transition-colors"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="h-12 w-full bg-[#d7fec8] hover:bg-[#c9f3b9] text-[#1b1b1b] font-bold rounded-xl transition-all shadow-lg shadow-[#d7fec8]/40 flex items-center justify-center gap-2"
                  >
                    {loading && (
                      <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true"></div>
                    )}
                    {loading ? 'Sending...' : 'Send Link'}
                  </button>
                </form>

                <button
                  onClick={() => {
                    setView('login');
                    setError(null);
                    setMessage(null);
                    setHoneypot('');
                  }}
                  className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-primary transition-colors group"
                >
                  <span className="material-symbols-outlined text-sm transition-transform group-hover:-translate-x-1" aria-hidden="true">
                    arrow_back
                  </span>
                  Back to Login
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-8 text-xs text-slate-500 text-center max-w-[280px]">
          By continuing, you agree to GoodLink's{' '}
          <button
            type="button"
            onClick={() => setLegalModalType('terms')}
            className="underline text-[#1b1b1b] hover:text-primary"
          >
            Terms of Service
          </button>{' '}
          and{' '}
          <button
            type="button"
            onClick={() => setLegalModalType('privacy')}
            className="underline text-[#1b1b1b] hover:text-primary"
          >
            Privacy Policy
          </button>
          .
        </div>
      </main>
      <Modal
        isOpen={!!legalModalType}
        onClose={() => setLegalModalType(null)}
        title={legalModalType === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
        message={
          legalModalType === 'terms' ? (
            <div className="text-left max-h-[70vh] overflow-y-auto pr-2">
              <TermsContent />
            </div>
          ) : legalModalType === 'privacy' ? (
            <div className="text-left max-h-[70vh] overflow-y-auto pr-2">
              <PrivacyContent />
            </div>
          ) : (
            ''
          )
        }
        type="info"
      />
    </div>
  );
};

export default AuthPage;



